import type { ActionTree, Store } from 'vuex';
import type { PageType, UnpublishState, } from '../type';

import { Api, PAGE_TYPES, PLAN_NAMES } from '@/modules/common';
import { base } from '@/services';
import { actionTypes, mutationTypes, getterTypes } from './types';
import { RootState } from '@/store';
import { MODULE_NAME } from '../const';
import { mutations as appMutationTypes, actions as appActions } from '@/store/app/types';
import { mutations as iframeMutationTypes } from '@/store/iframe/types';
import { disableNavBarAction, enableNavBarAction } from '../utils/dom-helper';
import { sortPage } from '../utils/sort-helper';

const actions: ActionTree<UnpublishState, UnpublishState> & any = {
  async [actionTypes.GET_PUBLISHED_PAGE](store: Store<UnpublishState>, payload: { shop: string, pageType: string, pagePlan: string }) {
    return new Promise(async (resolve, reject) => {
      try {
        if (payload.pagePlan === PLAN_NAMES.BUILD) {
          const baseUrl = payload.pageType === PAGE_TYPES.PRODUCT_PAGE ? base.template.product : base.template.article;
          const { data: publishedPages } = await Api.base.get(baseUrl, { params: { published: 1 }});

          let publishedPage: PageType[] = [];
          if (publishedPages && publishedPages.pages) {
            publishedPage = publishedPages.pages.map((page: any) => ({
              id: page.id,
              title: page.title,
              isDefault: page.published,
              isScheduled: !!(page.events.some((event: any) => event.event == 'schedule_publish')) ? 1 : 0,
              isInCampaign: !!page.runningCampaignId ? page.runningCampaignId : 0,
              lastUpdated: page.updated_at,
              thumbnail: page.thumbnail_url,
              type: page.type,
              numberOfAssignedProduct: page.assignmentCount
            })).sort((pageA: PageType, pageB: PageType) => sortPage(pageA, pageB));
            if (payload.pageType == PAGE_TYPES.ARTICLE_PAGE) {
              store.commit(mutationTypes.STORE_PUBLISHED_PAGE, { publishedPage });
              resolve(true);
            }

            store.commit(mutationTypes.STORE_PUBLISHED_PAGE, { publishedPage });
            resolve(true);
          }
        } else {
          let publishedPage: PageType[] = [];
          const { data: publishedPages } = await Api.base.get(base.getPublishedPage);
          if (publishedPages && publishedPages.pages) {
            publishedPage = publishedPages.pages.map((page: any) => ({
              id: page.id,
              title: page.title,
              isDefault: page.published,
              isScheduled: !!(page.events.some((event: any) => event.event == 'schedule_publish')) ? 1 : 0,
              isInCampaign: !!page.runningCampaignId ? page.runningCampaignId : 0,
              lastUpdated: page.updated_at,
              thumbnail: page.thumbnail_url,
              type: page.type,
            })).sort((pageA: PageType, pageB: PageType) => sortPage(pageA, pageB));
            store.commit(mutationTypes.STORE_PUBLISHED_PAGE, { publishedPage });
            resolve(true);
          }
        }
      } catch (error) {
        store.commit(mutationTypes.SET_IS_LOADING, false);
        reject(error);
      }
    });
  },
  async [actionTypes.UNPUBLISH_PAGES](this: Store<RootState>) {
    return new Promise(async (resolve, reject) => {
      try {
        const removePageIds = this.getters[`${MODULE_NAME}/${getterTypes.GET_UNPUBLISH_LIST}`];
        let isUseSocket = window._webSocket && window._webSocket.connection.readyState === 1 ? true : false;
        if (removePageIds) {
          const pageType = this.getters['app/pageType'];
          const pagePlan = this.getters['app/pagePlan'];
          const limitPublishPage = this.getters['app/limitPublishPage'];
          const publishedPage = this.getters['app/publishedPage'];
          disableNavBarAction();
          this.commit(`${MODULE_NAME}/${mutationTypes.SET_IS_USING_WEB_SOCKET}`, isUseSocket);
          this.commit(`${MODULE_NAME}/${mutationTypes.SET_IS_OPEN_MODAL}`, false);
          this.commit(`iframe/${iframeMutationTypes.ALERT}`, {
            show: true,
            title: '',
            message: `Unpublishing <b>${removePageIds.length}</b> pages`,
            typeAlert: 'info',
            delay: 'infinity',
            hideCloseIcon: true,
          });
          await Api.base.post(base.unpublish, { ids: removePageIds.join(','), isUseSocket });
          if (!isUseSocket) {
            await this.dispatch(`app/${appActions.GET_PAGE_INFO}`);
            const message = publishedPage - removePageIds.length < limitPublishPage
              ? 'Unpublishing successfully. You can now publish this page.'
              : `Unpublishing <b>${removePageIds.length}</b> successfully.`
            this.commit(`iframe/${iframeMutationTypes.ALERT}`, {
              show: true,
              title: '',
              message: message,
              typeAlert: 'success',
            });
            if (pagePlan === PLAN_NAMES.BUILD) {
              if (pageType == PAGE_TYPES.PRODUCT_PAGE) {
                this.commit(`app/${appMutationTypes.DECREASE_NUMBER_OF_PUBLISHED_PRODUCT_PAGE}`, removePageIds.length);
              }
              else {
                this.commit(`app/${appMutationTypes.DECREASE_NUMBER_OF_PUBLISHED_ARTICLE_PAGE}`, removePageIds.length);
              }
            } else {
              this.commit(`app/${appMutationTypes.DECREASE_NUMBER_OF_PUBLISHED_PAGE}`, removePageIds.length);
            }
            this.commit(`${MODULE_NAME}/${mutationTypes.RESET_UNPUBLISH_DATA}`);
            enableNavBarAction();
          }
        }

        resolve(true);
      } catch (error) {
        this.commit(`iframe/${iframeMutationTypes.ALERT}`, {
          show: true,
          title: '',
          message: 'Failed to unpublish page',
          typeAlert: 'error',
        });
        this.commit(`${MODULE_NAME}/${mutationTypes.SET_IS_USING_WEB_SOCKET}`, undefined);
        // eslint-disable-next-line no-console
        console.log(error);
        enableNavBarAction();
        reject(error);
      }
    })
  },
  async [actionTypes.STOP_AB_CAMPAIGN](store: Store<UnpublishState>, campaignId: number) {
    return new Promise(async (resolve, reject) => {
      try {
        store.commit(mutationTypes.ADD_CAMPAIGN, campaignId);
        await Api.base.post(base.stopCampaign(campaignId));
        store.commit(mutationTypes.REMOVE_CAMPAIGN_STATUS, campaignId);
        resolve(true);
      } catch (error) {
        store.commit(mutationTypes.CANCEL_CAMPAIGN_STATUS, campaignId);
        // eslint-disable-next-line no-console
        console.log(error);
        reject(error);
      }
    })
  }
}

export default actions;
